<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="flex gap-4 h-full">
    <!-- 左侧：字段面板 -->
    <div class="w-40 shrink-0 bg-slate-50 rounded-xl p-3 border border-slate-200 overflow-y-auto">
      <h4 class="text-xs font-semibold text-slate-500 uppercase mb-3">拖拽添加</h4>
      <div class="space-y-1.5">
        <div
          v-for="field in fieldTypes"
          :key="field.type"
          draggable="true"
          @dragstart="handleDragStart($event, field.type)"
          class="px-3 py-2 bg-white rounded-lg border border-slate-200 text-xs text-slate-600 cursor-move hover:border-primary-400 hover:bg-primary-50 transition-colors flex items-center gap-2"
        >
          <component :is="field.icon" class="w-4 h-4 text-slate-400" />
          {{ field.label }}
        </div>
      </div>
    </div>

    <!-- 中间：表单画布 -->
    <div class="flex-1 bg-slate-50 rounded-xl p-3 border border-slate-200 overflow-y-auto">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-xs font-semibold text-slate-500 uppercase">字段列表</h4>
        <span class="text-xs text-slate-400">拖放到此处</span>
      </div>

      <div
        @dragover.prevent="handleDragOver"
        @drop="handleDrop"
        @dragleave="handleDragLeave"
        class="min-h-[300px] bg-white rounded-lg border-2 border-dashed transition-colors p-3"
        :class="isDragOver ? 'border-primary-400 bg-primary-50' : 'border-slate-200'"
      >
        <div v-if="fields.length === 0" class="h-full flex items-center justify-center text-slate-400 text-xs">
          拖拽左侧字段类型添加
        </div>

        <div v-else class="space-y-2">
          <div
            v-for="(field, index) in fields"
            :key="field.name"
            class="bg-white rounded-lg border border-slate-200 p-3 hover:border-primary-300 transition-colors"
          >
            <div class="flex items-center gap-3">
              <!-- 字段图标 -->
              <div class="text-slate-400">
                <component :is="getFieldIcon(field.type)" class="w-4 h-4" />
              </div>

              <!-- 标题 -->
              <input
                v-model="field.title"
                type="text"
                class="flex-1 px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="请输入字段标题"
                @input="onTitleChange(field)"
                @change="updateModelValue"
              >

              <!-- 类型 -->
              <select
                v-model="field.type"
                class="w-20 px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                @change="updateModelValue"
              >
                <option v-for="ft in fieldTypes" :key="ft.type" :value="ft.type">
                  {{ ft.label }}
                </option>
              </select>

              <!-- 必填 -->
              <label class="flex items-center gap-1 cursor-pointer">
                <input
                  v-model="field.required"
                  type="checkbox"
                  class="w-3 h-3 text-primary-600 border-slate-300 rounded"
                  @change="updateModelValue"
                >
                <span class="text-xs text-slate-500">必填</span>
              </label>

              <!-- 删除 -->
              <button
                @click="removeField(index)"
                class="p-1 text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 扩展配置 -->
            <div v-if="field.type === 'select'" class="mt-2 ml-7">
              <input
                v-model="field.enumValues"
                type="text"
                class="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="枚举值：选项1,选项2,选项3"
                @change="updateModelValue"
              >
            </div>

            <div v-if="field.type === 'date'" class="mt-2 ml-7">
              <select
                v-model="field.format"
                class="px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                @change="updateModelValue"
              >
                <option value="date">日期</option>
                <option value="datetime">日期时间</option>
                <option value="time">时间</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧：JSON预览 -->
    <div class="w-64 shrink-0 bg-slate-50 rounded-xl p-3 border border-slate-200 overflow-y-auto">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-xs font-semibold text-slate-500 uppercase">Schema</h4>
        <button
          @click="copySchema"
          class="px-2 py-1 text-xs text-primary-600 hover:bg-primary-50 rounded transition-colors cursor-pointer"
        >
          复制
        </button>
      </div>
      <pre class="text-xs text-slate-600 bg-white rounded p-2 overflow-auto max-h-[400px] font-mono">{{ schemaJson }}</pre>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, h } from 'vue'
import { ElMessage } from 'element-plus'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      type: 'object',
      properties: {},
      required: []
    })
  }
})

const emit = defineEmits(['update:modelValue'])

const isDragOver = ref(false)
const draggingType = ref(null)

// 字段类型定义
const fieldTypes = [
  { type: 'text', label: '文本' },
  { type: 'number', label: '数字' },
  { type: 'date', label: '日期' },
  { type: 'select', label: '下拉' },
  { type: 'textarea', label: '多行' },
  { type: 'email', label: '邮箱' },
  { type: 'phone', label: '手机' }
]

// 字段列表
const fields = reactive([])

// 初始化字段
function initFields() {
  fields.length = 0
  if (props.modelValue?.properties) {
    Object.entries(props.modelValue.properties).forEach(([name, config]) => {
      fields.push({
        name,
        title: config.title || name,
        type: config.type || 'text',
        format: config.format || '',
        enumValues: config.enumValues ? config.enumValues.join(',') : '',
        required: props.modelValue.required?.includes(name) || false
      })
    })
  }
}

// 监听 modelValue 变化
watch(() => props.modelValue, () => {
  initFields()
}, { immediate: true, deep: true })

// 获取字段图标
function getFieldIcon(type) {
  const icons = {
    text: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M4 6h16M4 12h16M4 18h7' })
    ]),
    number: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14' })
    ]),
    date: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' })
    ]),
    select: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M19 9l-7 7-7-7' })
    ]),
    textarea: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M4 6h16M4 12h16M4 18h12' })
    ]),
    email: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' })
    ]),
    phone: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' })
    ])
  }
  return icons[type] || icons.text
}

// 拖拽处理
function handleDragStart(event, type) {
  draggingType.value = type
  event.dataTransfer.effectAllowed = 'copy'
}

function handleDragOver(event) {
  event.dataTransfer.dropEffect = 'copy'
  isDragOver.value = true
}

function handleDragLeave() {
  isDragOver.value = false
}

function handleDrop() {
  isDragOver.value = false
  if (!draggingType.value) return

  const type = draggingType.value
  const existingNames = fields.map(f => f.name)

  // 生成唯一字段名
  let baseName = `${type}_field`
  let name = baseName
  let counter = 1
  while (existingNames.includes(name)) {
    name = `${baseName}_${counter++}`
  }

  // 根据类型生成默认标题和格式
  const typeLabels = {
    text: '文本',
    number: '数字',
    date: '日期',
    select: '下拉选择',
    textarea: '多行文本',
    email: '邮箱',
    phone: '手机号'
  }
  const defaultTitle = typeLabels[type] || '字段'

  // date类型默认格式为date
  const defaultFormat = type === 'date' ? 'date' : ''

  fields.push({
    name,
    title: defaultTitle,
    type,
    format: defaultFormat,
    enumValues: '',
    required: false
  })

  draggingType.value = null
  updateModelValue()
}

// 标题变化时自动生成字段名
function onTitleChange(field) {
  if (!field.title || !field.title.trim()) {
    // 标题为空时，不修改字段名，保持原值
    return
  }
  // 将标题转为合法的字段名：去除空格、特殊字符，转为下划线分隔
  field.name = field.title
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_\u4e00-\u9fa5]/g, '')
    .toLowerCase()
}

function removeField(index) {
  fields.splice(index, 1)
  updateModelValue()
}

function updateModelValue() {
  const properties = {}
  const required = []

  fields.forEach(field => {
    const fieldConfig = { type: field.type, title: field.title }
    if (field.type === 'select' && field.enumValues) {
      fieldConfig.enumValues = field.enumValues.split(',').map(v => v.trim()).filter(Boolean)
    }
    if (field.type === 'date') {
      fieldConfig.format = field.format || 'date'
    }
    properties[field.name] = fieldConfig
    if (field.required) {
      required.push(field.name)
    }
  })

  emit('update:modelValue', {
    type: 'object',
    properties,
    required: required.length > 0 ? required : undefined
  })
}

const schemaJson = computed(() => JSON.stringify(props.modelValue, null, 2))

function copySchema() {
  navigator.clipboard.writeText(schemaJson.value).then(() => {
    ElMessage.success('已复制')
  }).catch(() => {
    ElMessage.error('复制失败')
  })
}
</script>
